import type { Routemize_DTOs_SlotSuggestionDTO } from '../../api/models/Routemize_DTOs_SlotSuggestionDTO';

/**
 * The detour rule caps the extra driving an appointment is allowed to add to a day
 * that already has appointments on it. Booking forms store the cap in miles as
 * `customerPreferenceMileageConstraint`.
 *
 * It is deliberately a display-side rule. The engine always returns every slot it
 * found; public booking surfaces drop the ones that break the cap so customers are
 * steered onto route-friendly times, while internal staff keep seeing everything
 * and decide for themselves whether the drive is worth it.
 */

/**
 * `reported` came from the engine and is exact. `estimated` is the client-side
 * upper bound computed in `estimateDetourMiles`.
 */
export type DetourBasis = 'reported' | 'estimated';

export interface SlotDetour {
  /** Extra miles this appointment adds to its day. */
  miles: number;
  basis: DetourBasis;
}

/**
 * Names the engine may report the figure under, matched as substrings of the
 * normalised metric name. Both `key` and `label` are checked: `RouteMetricDTO`
 * carries both and the engine has shipped a metric's machine name in `label` with
 * `key` left unset before, which is invisible to a reader that only looks at one.
 */
const REPORTED_METRIC_NAMES = ['marginaldetour', 'detourmiles', 'addedmiles', 'extramiles'];

/** Tells `marginalDetourMinutes` apart from the miles figure sitting next to it. */
const DURATION_METRIC_HINTS = ['minute', 'time'];

const toPositiveMiles = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

/**
 * The engine describes the drive to and from a slot the same way whether the other
 * end is a real appointment or the crew's own home, so both have to be told apart by
 * inspection. A home leg either carries no start time or names itself in the
 * location, and the engine is not consistent about which — a slot on an empty day
 * still gets timestamps off the workday bounds. Either signal alone therefore
 * mistakes an empty day for a busy one.
 */
const HOME_LEG_LOCATION = /\bhome\b|start of day|workday start|end of day|return to home/i;

const isHomeLeg = (location: string | null | undefined, startTimes: (string | null | undefined)[]): boolean =>
  HOME_LEG_LOCATION.test(location || '') || !startTimes.some(Boolean);

const hasPreviousAppointment = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean =>
  !isHomeLeg(slot.previousAppointmentLocation, [
    slot.previousAppointmentStartTime,
    slot.previousAppointmentStartTimeUtc,
  ]);

const hasNextAppointment = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean =>
  !isHomeLeg(slot.nextAppointmentLocation, [
    slot.nextAppointmentStartTime,
    slot.nextAppointmentStartTimeUtc,
  ]);

/**
 * True when the slot would be the only appointment on its day. Those miles are the
 * unavoidable cost of servicing the address at all rather than a detour, so the cap
 * never applies — how far the crew will travel from scratch is the service radius
 * in Route Preferences.
 */
export const isOnlyAppointmentOfDay = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean =>
  !hasPreviousAppointment(slot) && !hasNextAppointment(slot);

/**
 * True when the slot is wedged between two committed stops, which is the only shape
 * `estimateDetourMiles` can say anything useful about.
 */
const isBetweenTwoAppointments = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean =>
  hasPreviousAppointment(slot) && hasNextAppointment(slot);

const namesReportedDetourMiles = (raw: string | null | undefined): boolean => {
  const normalised = (raw || '').replace(/[^a-z]/gi, '').toLowerCase();
  if (!normalised) return false;
  if (DURATION_METRIC_HINTS.some((hint) => normalised.includes(hint))) return false;
  return REPORTED_METRIC_NAMES.some((name) => normalised.includes(name));
};

const readReportedDetour = (slot: Routemize_DTOs_SlotSuggestionDTO): number | null => {
  for (const metric of slot.routeMetrics ?? []) {
    if (!namesReportedDetourMiles(metric.key) && !namesReportedDetourMiles(metric.label)) continue;
    const miles = Number.parseFloat(String(metric.value ?? ''));
    if (Number.isFinite(miles) && miles >= 0) return miles;
  }
  return null;
};

/**
 * Upper bound on the miles this appointment adds to its day.
 *
 * The exact figure is `driveIn + driveOut` minus the leg the crew would have driven
 * between those two neighbours anyway, and that third leg is not on the slot
 * payload. The triangle inequality puts it at no less than `|driveIn - driveOut|`,
 * which bounds the detour at `2 * min(driveIn, driveOut)`. The bound is tight when
 * the appointment sits off to one side of the route — the case worth blocking — and
 * loosest when it sits between its neighbours, where it overstates the cost.
 *
 * Both legs have to be known. Guessing from one would be a number in name only, and
 * an unmeasurable slot is never hidden.
 */
const estimateDetourMiles = (slot: Routemize_DTOs_SlotSuggestionDTO): number | null => {
  const inbound = toPositiveMiles(slot.previousAppointmentDistanceMiles);
  const outbound = toPositiveMiles(slot.nextAppointmentDistanceMiles);
  if (inbound == null || outbound == null) return null;
  return 2 * Math.min(inbound, outbound);
};

/**
 * The extra driving this slot costs, or null when it cannot be measured.
 *
 * A figure from the engine is trusted wherever it appears, because it compares the
 * whole day with and without the appointment and so already accounts for the drive
 * out from home and back. The estimate has no such view: it treats the home leg as
 * if it were another appointment, which turns a stop on the way back into a detour
 * as long as the drive home itself. So the estimate is only offered between two
 * committed stops — the shape where a detour genuinely exists, and where the bound
 * is tightest anyway. Everywhere else it declines to guess and the slot goes
 * unmeasured, governed by the service radius in Route Preferences instead.
 */
export const getSlotDetour = (
  slot: Routemize_DTOs_SlotSuggestionDTO | null | undefined
): SlotDetour | null => {
  if (!slot) return null;

  const reported = readReportedDetour(slot);
  if (reported != null) return { miles: reported, basis: 'reported' };

  if (!isBetweenTwoAppointments(slot)) return null;

  const estimated = estimateDetourMiles(slot);
  return estimated == null ? null : { miles: estimated, basis: 'estimated' };
};

/**
 * Whether the slot breaks the form's detour cap. Fails open on every uncertainty —
 * no cap set, an otherwise empty day, or driving we could not measure all count as
 * within the limit, so the rule can only ever hide a slot we are confident about.
 */
export const exceedsDetourLimit = (
  slot: Routemize_DTOs_SlotSuggestionDTO | null | undefined,
  limitMiles: number | null | undefined
): boolean => {
  if (!slot) return false;
  if (limitMiles == null || !Number.isFinite(limitMiles) || limitMiles <= 0) return false;
  if (isOnlyAppointmentOfDay(slot)) return false;

  const detour = getSlotDetour(slot);
  return detour != null && detour.miles > limitMiles;
};

/**
 * Detour worth telling staff about whatever the booking form is set to. Above this
 * the drive is long enough to matter on its own, so it is flagged even on a form
 * with no cap configured.
 */
export const ALWAYS_FLAG_DETOUR_MILES = 40;

export interface DetourWarning extends SlotDetour {
  /** True when the cap would have kept this slot off the customer-facing surfaces. */
  hiddenFromCustomers: boolean;
}

/**
 * What to warn staff about on the slot picker, or null when the drive is not worth
 * saying anything about. Two independent reasons to warn: the cap would have hidden
 * the slot from customers, or the detour is long in absolute terms.
 */
export const getDetourWarning = (
  slot: Routemize_DTOs_SlotSuggestionDTO | null | undefined,
  limitMiles: number | null | undefined
): DetourWarning | null => {
  if (!slot) return null;
  if (isOnlyAppointmentOfDay(slot)) return null;

  const detour = getSlotDetour(slot);
  if (!detour) return null;

  const hiddenFromCustomers = exceedsDetourLimit(slot, limitMiles);
  if (!hiddenFromCustomers && detour.miles < ALWAYS_FLAG_DETOUR_MILES) return null;

  return { ...detour, hiddenFromCustomers };
};

/**
 * Reads the cap off whichever settings shape the caller happens to hold. Internal
 * surfaces get the full `BookingFormSettingDto`, public surfaces the narrower
 * `PublicBookingFormSettingsDTO`; both carry the field, and a settings object that
 * somehow lacks it leaves the rule dormant rather than failing the call site.
 */
export const readDetourLimitMiles = (settings: unknown): number | null => {
  const value = (settings as { customerPreferenceMileageConstraint?: unknown } | null | undefined)
    ?.customerPreferenceMileageConstraint;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Drops the slots that break the cap, for customer-facing surfaces only.
 *
 * Scoped by the drive rather than by the category the engine filed the slot under. A
 * company or proximity slot is picked around the route and should not trip the cap
 * on its own, so in practice this removes the same times either way — but it no
 * longer depends on `slotType` surviving the trip to the public payload, and a slot
 * that does cost 90 miles is hidden whatever it was labelled.
 *
 * The cap is a hard rule (task 86bbjbnma): an over-cap slot is never offered as
 * bookable, even if that leaves nothing. Callers already fall back to their
 * manual-review / request flow when the result is empty.
 */
export const filterSlotsForPublicDisplay = (
  slots: Routemize_DTOs_SlotSuggestionDTO[] | null | undefined,
  limitMiles: number | null | undefined
): Routemize_DTOs_SlotSuggestionDTO[] => {
  if (!slots?.length) return slots ?? [];
  if (limitMiles == null || !Number.isFinite(limitMiles) || limitMiles <= 0) return slots;

  return slots.filter((slot) => !exceedsDetourLimit(slot, limitMiles));
};
